import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, CheckCircle2, Image as ImageIcon, Send, UserRound } from "lucide-react";
import { toast, Toaster } from "sonner";
import api from "@/lib/api";

export default function GuideSubmit() {
  const { token } = useParams();
  const [checking, setChecking] = useState(true);
  const [available, setAvailable] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [form, setForm] = useState({ name: "", profession: "", place: "", batch: "", phone: "", bio: "" });
  const [photo, setPhoto] = useState(null);

  const photoPreview = useMemo(() => (photo ? URL.createObjectURL(photo) : ""), [photo]);
  useEffect(() => () => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
  }, [photoPreview]);

  useEffect(() => {
    api.get(`/guide-invites/${token}`)
      .then(() => setAvailable(true))
      .catch((err) => setError(err?.response?.data?.detail || "This guide submission link is no longer available."))
      .finally(() => setChecking(false));
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      toast.error("Please enter your name");
      return;
    }
    const phoneDigits = form.phone.replace(/\D/g, "");
    if (phoneDigits.length < 7 || phoneDigits.length > 15) {
      toast.error("Please enter a valid phone number");
      return;
    }
    if (!photo) {
      toast.error("Please upload your guide photo");
      return;
    }
    setSending(true);
    try {
      const uploadData = new FormData();
      uploadData.append("file", photo);
      const uploadRes = await api.post(`/guide-invites/${token}/upload`, uploadData, { headers: { "Content-Type": "multipart/form-data" } });
      const { data } = await api.post(`/guide-invites/${token}/submit`, {
        ...form,
        name: form.name.trim(),
        profession: form.profession.trim(),
        place: form.place.trim(),
        batch: form.batch.trim(),
        phone: form.phone.trim(),
        bio: form.bio.trim(),
        photo_url: uploadRes.data.url,
      });
      setSubmitted(true);
      toast.success(data.message || "Guide details submitted");
    } catch (err) {
      const message = err?.response?.data?.detail || "Could not submit guide details. Please try again.";
      setError(message);
      toast.error(message);
      if (err?.response?.status === 410) setAvailable(false);
    } finally {
      setSending(false);
    }
  };

  return (
    <main data-testid="guide-submit-page" className="relative min-h-screen overflow-hidden bg-[#F4F1EA] text-[#1A2E26]">
      <Toaster position="top-center" richColors />
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-[#F4F1EA] via-[#EDECE3] to-[#E4EAE0]" />
      <div className="absolute -z-10 top-[-12%] right-[-8%] w-[48vw] h-[48vw] rounded-full bg-[#A3B19B]/30 blur-[120px]" aria-hidden="true" />
      <div className="mx-auto grid min-h-screen max-w-2xl place-items-center px-6 py-10">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full rounded-3xl border border-white/60 bg-white/75 p-7 shadow-[0_8px_40px_rgba(26,46,38,0.08)] backdrop-blur md:p-10"
        >
          <Link to="/" data-testid="guide-submit-home-link" className="mb-8 inline-flex items-center gap-2 text-sm text-[#1A2E26]/70 transition-colors hover:text-[#C86A51]">
            <ArrowLeft size={16} /> Positive Life
          </Link>

          {checking ? (
            <p data-testid="guide-submit-loading" className="py-10 text-center text-muted-foreground">Checking your guide link…</p>
          ) : submitted ? (
            <div data-testid="guide-submit-success" className="py-8 text-center">
              <span className="mx-auto mb-5 grid h-16 w-16 place-items-center rounded-full bg-[#128C4B]/10 text-[#128C4B]"><CheckCircle2 size={30} /></span>
              <h1 className="font-serif text-3xl md:text-4xl">Thank you.</h1>
              <p className="mt-4 leading-relaxed text-[#1A2E26]/70">Your guide details were submitted and will be visible after admin approval. This link is now closed.</p>
            </div>
          ) : !available ? (
            <div data-testid="guide-submit-unavailable" className="py-8 text-center">
              <h1 className="font-serif text-3xl md:text-4xl">This guide link is closed.</h1>
              <p className="mt-4 text-[#1A2E26]/70">{error || "Please contact the Positive Life admin for a new link."}</p>
            </div>
          ) : (
            <>
              <p className="mb-3 text-xs uppercase tracking-[0.3em] text-[#C86A51]">Guide details</p>
              <h1 className="font-serif text-3xl md:text-4xl leading-tight">Share your guide details.</h1>
              <p className="mb-8 mt-3 text-sm leading-relaxed text-[#1A2E26]/70">Upload your photo and fill your details. Admin will review them before they appear publicly. This link works only once.</p>
              <form onSubmit={submit} data-testid="guide-submit-form" className="space-y-5">
                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground">Full name *</label>
                  <input required data-testid="guide-submit-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                    className="mt-2 w-full border-b border-border bg-transparent py-2.5 outline-none transition-colors focus:border-accent" placeholder="Your name" />
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-muted-foreground">Profession</label>
                    <input data-testid="guide-submit-profession" value={form.profession} onChange={(e) => setForm({ ...form, profession: e.target.value })}
                      className="mt-2 w-full border-b border-border bg-transparent py-2.5 outline-none transition-colors focus:border-accent" placeholder="What you do" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-muted-foreground">Place</label>
                    <input data-testid="guide-submit-place" value={form.place} onChange={(e) => setForm({ ...form, place: e.target.value })}
                      className="mt-2 w-full border-b border-border bg-transparent py-2.5 outline-none transition-colors focus:border-accent" placeholder="City / town" />
                  </div>
                </div>
                <div className="grid gap-5 sm:grid-cols-2">
                  <div>
                    <label className="text-xs uppercase tracking-widest text-muted-foreground">Phone number *</label>
                    <input required type="tel" data-testid="guide-submit-phone" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="mt-2 w-full border-b border-border bg-transparent py-2.5 outline-none transition-colors focus:border-accent" placeholder="Your phone number" />
                  </div>
                  <div>
                    <label className="text-xs uppercase tracking-widest text-muted-foreground">Batch (optional)</label>
                    <input data-testid="guide-submit-batch" value={form.batch} onChange={(e) => setForm({ ...form, batch: e.target.value })}
                      className="mt-2 w-full border-b border-border bg-transparent py-2.5 outline-none transition-colors focus:border-accent" placeholder="e.g. Batch 100" />
                  </div>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground">Photo *</label>
                  <label data-testid="guide-submit-photo-label" className="mt-2 flex cursor-pointer items-center gap-3 rounded-2xl border border-dashed border-border bg-background/60 px-4 py-4 transition-colors hover:border-accent">
                    {photoPreview ? (
                      <img src={photoPreview} alt="Guide preview" data-testid="guide-submit-photo-preview" className="h-16 w-16 rounded-xl object-cover" />
                    ) : (
                      <span className="grid h-16 w-16 place-items-center rounded-xl bg-accent/10 text-accent"><ImageIcon size={24} /></span>
                    )}
                    <span className="text-sm text-muted-foreground">{photo ? photo.name : "Upload a clear photo from your phone"}</span>
                    <input type="file" accept="image/*" data-testid="guide-submit-photo" className="hidden" onChange={(e) => setPhoto(e.target.files?.[0] || null)} />
                  </label>
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-muted-foreground">How you help participants</label>
                  <textarea rows={3} data-testid="guide-submit-bio" value={form.bio} onChange={(e) => setForm({ ...form, bio: e.target.value })}
                    className="mt-2 w-full resize-none rounded-2xl border border-border bg-transparent px-4 py-3 outline-none transition-colors focus:border-accent" placeholder="A short note for the admin" />
                </div>
                <button type="submit" disabled={sending} data-testid="guide-submit-button"
                  className="inline-flex w-full items-center justify-center gap-2 rounded-full bg-primary px-8 py-3.5 text-sm text-primary-foreground transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60">
                  <UserRound size={16} /> {sending ? "Submitting…" : "Submit guide details"} <Send size={15} />
                </button>
              </form>
            </>
          )}
        </motion.div>
      </div>
    </main>
  );
}
